import type { MouseEvent, ReactNode } from "react";

import { classes } from "./classes.js";

/**
 * `D-1019-4`: **a row of counted stages, drawn as one band.**
 *
 * The founder's sitting of 19 October: ten equal boxes, eight of them at « 0 / aucun dossier », and the
 * name of the first wrapping so its count sat lower than its neighbours'. A strip is not ten cards; it is
 * one band with thin separators, and what it says is what is IN it:
 *
 *  - every cell has the same width (`flex-1 basis-0`) and the name keeps TWO lines whether it uses them or
 *    not (`h-8`), so the counts share one line whatever the names do;
 *  - the count is `tabular-nums`, so a column of counts reads as a column;
 *  - a stage with nothing in it is muted and silent: its name and its zero, no subtitle, and nothing to
 *    click, because the list filtered on it would be empty;
 *  - a stage with files is a LINK (`href`), under no underline, and says « à l'heure » or « N en retard »
 *    in the alert colour; a plain click is handed to `onSelect` so the app can route without a reload, and a
 *    modified click (new tab) is left to the browser;
 *  - the exits from the path, if any, sit on their own line below.
 *
 * **The component knows no stage, no filter and no language**: the caller gives the names, the counts, the
 * links and the words (4.6.2).
 */

export interface StripStage {
  readonly id: string;
  readonly name: string;
  readonly count: number;
  /** Files past the stage's expected duration. `0` for « à l'heure ». */
  readonly late: number;
  /** Where the stage leads when it has files: the list filtered on it. */
  readonly href: string;
}

export interface StripProps {
  /** The band's accessible name. */
  readonly label: string;
  readonly stages: readonly StripStage[];
  /** The stage whose filter is on, if any. */
  readonly current?: string | null | undefined;
  /** « à l'heure ». */
  readonly onTimeLabel: string;
  /** « 3 en retard ». */
  readonly lateLabel: (late: number) => string;
  /** A plain click on a stage with files; the app routes. Absent: the browser follows the link. */
  readonly onSelect?: ((id: string) => void) | undefined;
  /** The exits from the path, on their own line below. Only those with files are given. */
  readonly exits?: readonly StripStage[] | undefined;
  readonly exitsLabel?: ReactNode;
  readonly className?: string;
  /** Prefix of the cells' test ids: `${testIdPrefix}-${stage.id}`. */
  readonly testIdPrefix: string;
}

const plainClick = (event: MouseEvent): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export function Strip(props: StripProps): React.JSX.Element {
  const { stages, exits = [] } = props;
  const follow = (stage: StripStage) => (event: MouseEvent) => {
    if (props.onSelect === undefined || !plainClick(event)) return;
    event.preventDefault();
    props.onSelect(stage.id);
  };
  return (
    <div className={classes("w-full", props.className)}>
      <ul aria-label={props.label} className="flex items-stretch">
        {stages.map((stage, index) => {
          const filled = stage.count > 0;
          const current = props.current === stage.id;
          const body = (
            <>
              <span
                className={classes(
                  "block h-8 overflow-hidden text-xs leading-4",
                  filled ? "text-gray-700" : "text-gray-500",
                )}
              >
                {stage.name}
              </span>
              <span
                className={classes(
                  "block text-lg font-semibold leading-7 tabular-nums",
                  filled ? "text-gray-900" : "text-gray-500",
                )}
              >
                {stage.count}
              </span>
              {filled ? (
                <span
                  className={classes(
                    "block text-xs leading-4",
                    stage.late > 0 ? "font-medium text-rose-600" : "text-gray-500",
                  )}
                >
                  {stage.late > 0 ? props.lateLabel(stage.late) : props.onTimeLabel}
                </span>
              ) : null}
            </>
          );
          const cell = classes(
            "block w-full min-w-0 px-3 py-2",
            index === 0 ? "" : "border-l border-gray-200",
          );
          return (
            <li key={stage.id} className="flex min-w-0 flex-1 basis-0">
              {filled ? (
                <a
                  href={stage.href}
                  data-testid={`${props.testIdPrefix}-${stage.id}`}
                  aria-current={current ? "true" : undefined}
                  onClick={follow(stage)}
                  className={classes(cell, "no-underline hover:bg-gray-50", current ? "bg-indigo-50" : "")}
                >
                  {body}
                </a>
              ) : (
                <div data-testid={`${props.testIdPrefix}-${stage.id}`} className={cell}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {exits.length === 0 ? null : (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-2 text-xs">
          <span className="text-gray-500">{props.exitsLabel}</span>
          {exits.map((exit) => (
            <a
              key={exit.id}
              href={exit.href}
              data-testid={`${props.testIdPrefix}-${exit.id}`}
              aria-current={props.current === exit.id ? "true" : undefined}
              onClick={follow(exit)}
              className="font-medium text-gray-700 no-underline hover:text-gray-900"
            >
              {exit.name} <span className="tabular-nums">{exit.count}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
